import { DEFAULT_WAITWHILE_SERVICE_ID } from '../../config/constants'
import type { BookingState } from '../../store'
import type {
  CreateBookingPayload,
  CustomerResponse,
  UserDataErrors,
  UserInfo,
} from './UserData.types'

/** Input limits, mirroring the legacy Stencil screen. */
export const FIELD_MAX_LENGTH = {
  name: 100,
  email: 100,
  notes: 500,
} as const

/**
 * Same pattern as the legacy tool: rejects consecutive dots, requires a TLD of
 * at least two characters. Deliberately looser than a full RFC 5322 parser —
 * the API is the real authority.
 */
const EMAIL_PATTERN = /^(?!.*\.\.)[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const isValidEmail = (email: string): boolean =>
  EMAIL_PATTERN.test(email.trim())

export interface ValidationMessages {
  required: string
  invalidEmail: string
  requiredPhone: string
  invalidPhone: string
}

/**
 * Validates the whole form in one pass so submit and inline validation can
 * never disagree. Phone validity comes from intl-tel-input (via
 * `InputPhoneNumber`), which knows per-country number formats.
 */
export const validateUserInfo = (
  user: UserInfo,
  isPhoneValid: boolean,
  messages: ValidationMessages,
): UserDataErrors => {
  const errors: UserDataErrors = {}

  if (!user.firstName.trim()) errors.firstName = messages.required
  if (!user.lastName.trim()) errors.lastName = messages.required

  if (!user.email.trim()) {
    errors.email = messages.required
  } else if (!isValidEmail(user.email)) {
    errors.email = messages.invalidEmail
  }

  if (!user.phone.trim()) {
    errors.phone = messages.requiredPhone
  } else if (!isPhoneValid) {
    errors.phone = messages.invalidPhone
  }

  return errors
}

/**
 * Maps a CRM customer onto the form's shape.
 *
 * Every field is filled in — the form is controlled, so an `undefined` would
 * turn a managed input into an uncontrolled one mid-render.
 *
 * `isSubscribed` keeps the newsletter box ticked by default, matching an
 * unprefilled form; only an explicit `false` from CRM unticks it.
 *
 * The phone comes across as-is, invalid or not. The legacy tool blanked
 * anything `libphonenumber-js` rejected, but `InputPhoneNumber` reports
 * validity for its initial value on mount, so a bad number on file now shows
 * with its error message — visible and fixable — instead of silently vanishing.
 */
export const toPrefilledUser = (customer: CustomerResponse): UserInfo => ({
  firstName: customer.firstName ?? '',
  lastName: customer.lastName ?? '',
  email: customer.email ?? '',
  phone: customer.phone ?? '',
  notes: customer.notes ?? '',
  isSubscribed: customer.isSubscribed ?? true,
})

/** Trims every free-text field so the API and the ehash see the same values. */
export const normalizeUserInfo = (user: UserInfo): UserInfo => ({
  ...user,
  firstName: user.firstName.trim(),
  lastName: user.lastName.trim(),
  email: user.email.trim(),
  notes: user.notes.trim(),
})

/**
 * Builds the `v2/booking/create` body from the booking store plus the collected
 * customer details.
 *
 * `groupSize` is always empty: the legacy screen showed a group-size dropdown
 * when `serviceId` matched `config.serviceIDs.group`, but that key does not
 * exist in any environment config — so the comparison was `undefined ===
 * undefined` and the dropdown surfaced on every flow that had no service
 * selected. The field is kept in the payload for API compatibility; re-enable
 * the dropdown once a real group service ID exists.
 */
export const buildCreateBookingPayload = (
  state: Pick<
    BookingState,
    | 'env'
    | 'timeslot'
    | 'serviceId'
    | 'wwResourceId'
    | 'wwLocationId'
    | 'locationCode'
    | 'location'
  >,
  user: UserInfo,
): CreateBookingPayload => {
  const {
    env,
    timeslot,
    serviceId,
    wwResourceId,
    wwLocationId,
    locationCode,
    location,
  } = state

  if (!timeslot) {
    throw new Error('Cannot create a booking without a selected timeslot')
  }

  return {
    email: user.email,
    phone: user.phone,
    firstName: user.firstName,
    lastName: user.lastName,
    date: timeslot,
    serviceIds: [serviceId ?? DEFAULT_WAITWHILE_SERVICE_ID[env]],
    // Waitwhile treats `null` as "any resource"; an empty array would fail.
    resourceIds: wwResourceId ? [wwResourceId] : null,
    locationId: wwLocationId,
    locationCode,
    notes: user.notes,
    location,
    groupSize: '',
    isSubscribed: user.isSubscribed,
  }
}

/**
 * SHA-256 hex digest of a value, sent to Avo as `ehash` so analytics can join
 * sessions without storing the address itself. The caller lowercases the
 * email first (DA-1554) so the same address always digests the same
 * regardless of capitalisation — this no longer matches the legacy tool's
 * byte-for-byte digest. Returns an empty string when Web Crypto is
 * unavailable (non-HTTPS, old browsers) — analytics must never block a
 * booking.
 */
export const sha256 = async (value: string): Promise<string> => {
  try {
    const subtle = globalThis.crypto?.subtle
    if (!subtle) return ''

    const bytes = new TextEncoder().encode(value)
    const digest = await subtle.digest('SHA-256', bytes)

    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return ''
  }
}
